import { useEventListener } from "@vueuse/core";
import { computed, type ComputedRef, ref } from "vue";

import { CommandActionType, CommandId, Key, ViewKind } from "~/shared/enums";
import type { Command, CommandSection, CommandShortcut } from "~/shared/types";
import { useAppStore, usePageStore } from "~/stores";
import type { InputElement } from "~/utils/DisabledGlobalShortcutsDirective";
import { printOrCaptureMessage } from "~/utils/exceptionClient";

type Shortcut = {
  id: CommandId;
  strokes: string[];
  action: () => void;
};
type Trie = Map<string, Shortcut | Map<string, Shortcut>>;
type SequenceState = { active: false } | { active: true; firstStroke: string; timestamp: number };

export const SEQUENCE_TIMEOUT = 600;
const INPUT_TAGS = new Set(["INPUT", "TEXTAREA"]);
const INPUT_ROLES = new Set(["textbox", "searchbox", "combobox"]);
const TEXT_INPUT_BLOCKED_COMMANDS = new Set([CommandId.UNDO, CommandId.REDO]);
const TEXT_INPUT_SHORTCUTS_DEFAULT = [
  { keys: [Key.Control, Key.A] }, // Select all
  { keys: [Key.Control, Key.C] }, // Copy
  { keys: [Key.Control, Key.X] }, // Cut
  { keys: [Key.Control, Key.V] }, // Paste
  { keys: [Key.Control, Key.Z] }, // Undo
  { keys: [Key.Control, Key.ArrowLeft] }, // Move by word
  { keys: [Key.Control, Key.ArrowRight] }, // Move by word
  { keys: [Key.Control, Key.Backspace] }, // Delete word backwards
  { keys: [Key.Control, Key.Delete] }, // Delete word forwards
  { keys: [Key.Control, Key.Shift, Key.ArrowLeft] }, // Select word left
  { keys: [Key.Control, Key.Shift, Key.ArrowRight] }, // Select word right
  { keys: [Key.Control, Key.Shift, Key.Home] }, // Select to start
  { keys: [Key.Control, Key.Shift, Key.End] }, // Select to end
  { keys: [Key.Control, Key.Home] }, // Move to start of document
  { keys: [Key.Control, Key.End] }, // Move to end of document
];
const TEXT_INPUT_SHORTCUTS_MAC_ONLY = [
  { keys: [Key.Control, Key.Shift, Key.Z] }, // Redo
  { keys: [Key.Control, Key.ArrowUp] }, // Move to start of field/document
  { keys: [Key.Control, Key.ArrowDown] }, // Move to end of field/document
  { keys: [Key.Control, Key.Shift, Key.ArrowUp] }, // Select to start
  { keys: [Key.Control, Key.Shift, Key.ArrowDown] }, // Select to end
];
const TEXT_INPUT_SHORTCUTS_NON_MAC_ONLY = [{ keys: [Key.Control, Key.Y] }]; // Redo
const TEXT_INPUT_SHORTCUTS: CommandShortcut = {
  default: [...TEXT_INPUT_SHORTCUTS_DEFAULT, ...TEXT_INPUT_SHORTCUTS_NON_MAC_ONLY],
  mac: [...TEXT_INPUT_SHORTCUTS_DEFAULT, ...TEXT_INPUT_SHORTCUTS_MAC_ONLY],
};
const LEXICAL_EDITOR_SHORTCUTS: CommandShortcut = {
  default: [
    { keys: [Key.Control, Key.Alt, Key.ArrowUp] }, // Move block up
    { keys: [Key.Control, Key.Alt, Key.ArrowDown] }, // Move block down
  ],
};

const logConflict = (strokes: string[], existingCommandId: CommandId, newCommandId: CommandId) => {
  printOrCaptureMessage("Shortcut key conflict", {
    strokes: strokes.join(" then "),
    existingCommandId: CommandId[existingCommandId],
    newCommandId: CommandId[newCommandId],
  });
};

const normalizeKeyCode = (code: string): string => code.replace(/^(Control|Shift|Alt|Meta)(Left|Right)$/, "$1");
const isModifierCode = (code: string): boolean =>
  code === "Control" || code === "Shift" || code === "Alt" || code === "Meta";

const stringifyStroke = (modifiers: Key[], key: string, isMac: boolean): string => {
  const parts: string[] = [];

  if (modifiers.includes(Key.Control)) {
    parts.push(isMac ? "Meta" : "Ctrl");
  }

  if (modifiers.includes(Key.Alt)) {
    parts.push("Alt");
  }

  if (modifiers.includes(Key.Shift)) {
    parts.push("Shift");
  }

  parts.push(key);

  return parts.join("+");
};

const stringifyStrokeFromEvent = (event: KeyboardEvent, isMac: boolean): string => {
  const { code, ctrlKey, metaKey, altKey, shiftKey } = event;
  const key = normalizeKeyCode(code);

  const modifiers: Key[] = [(ctrlKey || metaKey) && Key.Control, altKey && Key.Alt, shiftKey && Key.Shift].filter(
    (k): k is Key => !!k
  );

  return stringifyStroke(modifiers, key, isMac);
};

const shortcutToStrokes = (shortcut: CommandShortcut, isMac: boolean): string[][] => {
  const raw = isMac ? (shortcut.mac ?? shortcut.default) : shortcut.default;
  if (!raw) {
    return [];
  }

  const shortcuts = Array.isArray(raw) ? raw : [raw];
  return shortcuts.map((e) =>
    (e.then ? [e.keys, e.then] : [e.keys]).map((strokeKeys) => {
      const modifiers: Key[] = [];
      let regularKey: Key | null = null;

      strokeKeys.forEach((key) => {
        if (isModifierCode(key)) {
          modifiers.push(key);
        } else {
          regularKey = key;
        }
      });

      if (!regularKey) {
        printOrCaptureMessage("Invalid stroke with only modifiers", { strokeKeys });
        return "";
      }

      return stringifyStroke(modifiers, regularKey, isMac);
    })
  );
};

const isShortcutAllowed = (isMac: boolean, element: Element | null, commandId: CommandId, stroke: string): boolean => {
  if (!element) {
    return true;
  }

  if (element.hasAttribute("readonly") || element.hasAttribute("disabled")) {
    return true;
  }

  const inDartEditor = !!element.closest(".dart-editor");
  const inTextInput =
    INPUT_TAGS.has(element.tagName) ||
    INPUT_ROLES.has(element.getAttribute("role") ?? "") ||
    !!element.closest("[contenteditable]:not([contenteditable='false'])") ||
    inDartEditor;

  if (!inTextInput) {
    return true;
  }

  if (inDartEditor && shortcutToStrokes(LEXICAL_EDITOR_SHORTCUTS, isMac).flat().includes(stroke)) {
    return false;
  }

  const inputElement = element as InputElement;
  if (
    inputElement._disabledGlobalShortcuts &&
    (inputElement._disabledGlobalShortcuts === true || inputElement._disabledGlobalShortcuts.includes(commandId))
  ) {
    return false;
  }

  if (TEXT_INPUT_BLOCKED_COMMANDS.has(commandId)) {
    return false;
  }

  if (shortcutToStrokes(TEXT_INPUT_SHORTCUTS, isMac).flat().includes(stroke)) {
    return false;
  }

  if (stroke.includes("Ctrl+") || stroke.includes("Meta+")) {
    return true;
  }

  return false;
};

const buildTrieFromShortcuts = (shortcuts: Shortcut[]): Trie =>
  shortcuts.reduce((trie, shortcut) => {
    const { strokes, id } = shortcut;
    if (strokes.length === 0 || strokes.length > 2) {
      printOrCaptureMessage("Invalid strokes count", { commandId: CommandId[id] });
      return trie;
    }

    const [firstStroke, secondStroke] = strokes;
    const existingFirstStroke = trie.get(firstStroke);

    if (strokes.length === 1) {
      if (existingFirstStroke) {
        const conflictId = (
          existingFirstStroke instanceof Map ? [...existingFirstStroke.values()][0] : existingFirstStroke
        ).id;
        logConflict(strokes, conflictId, id);
      } else {
        trie.set(firstStroke, shortcut);
      }
      return trie;
    }

    if (existingFirstStroke && !(existingFirstStroke instanceof Map)) {
      logConflict(strokes, existingFirstStroke.id, id);
      return trie;
    }

    const secondStrokeMap = existingFirstStroke ?? new Map();
    if (!existingFirstStroke) {
      trie.set(firstStroke, secondStrokeMap);
    }

    const existingSecondStroke = secondStrokeMap.get(secondStroke);
    if (existingSecondStroke) {
      logConflict(strokes, existingSecondStroke.id, id);
    } else {
      secondStrokeMap.set(secondStroke, shortcut);
    }

    return trie;
  }, new Map());

const createShortcutCallback = (command: Command): (() => void) | null => {
  const appStore = useAppStore();

  const { action } = command;
  if (!action) {
    return null;
  }

  switch (action.type) {
    case CommandActionType.CALLBACK: {
      return () => action.callback("");
    }
    case CommandActionType.EXTERNAL_LINK: {
      return () => {
        if (action.url.startsWith("mailto:")) {
          window.location.href = action.url;
        } else {
          window.open(action.url, "_blank");
        }
      };
    }
    case CommandActionType.SELECT:
    case CommandActionType.MULTISELECT: {
      return () => appStore.setCommandCenterOpen(true, command.id);
    }
    default: {
      return null;
    }
  }
};

export const useKeyboardShortcuts = (commandSections: ComputedRef<CommandSection[]>, isMac: boolean) => {
  const appStore = useAppStore();
  const pageStore = usePageStore();
  const shortcuts = computed<Shortcut[]>(() =>
    commandSections.value.flatMap((e) =>
      e.commands.flatMap((command) => {
        const callback = createShortcutCallback(command);
        if (
          !callback ||
          !command.shortcut ||
          !command.shortcut.default ||
          !(command.filters ?? []).every((f) => f.value) ||
          (command.availabilityRules ?? []).some((rule) => !rule.value.value)
        ) {
          return [];
        }

        return shortcutToStrokes(command.shortcut, isMac).map((strokes) => ({
          id: command.id,
          strokes,
          action: callback,
        }));
      })
    )
  );
  const trie = computed(() => buildTrieFromShortcuts(shortcuts.value));

  const sequenceState = ref<SequenceState>({ active: false });
  const resetSequenceState = () => {
    sequenceState.value = { active: false };
  };
  const executeShortcut = (shortcut: Shortcut, event: KeyboardEvent, stroke: string) => {
    if (!isShortcutAllowed(isMac, document.activeElement, shortcut.id, stroke)) {
      return;
    }

    event.preventDefault();
    shortcut.action();
    resetSequenceState();
  };

  const ALLOWED_IN_TRASH_COMMANDS = new Set([CommandId.COPY_LINK, CommandId.DELETE_TASK]);
  const isShortcutAllowedOnPage = (commandId: CommandId) => {
    const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);
    return !isTrash.value || ALLOWED_IN_TRASH_COMMANDS.has(commandId);
  };

  useEventListener(window, "keydown", (event: KeyboardEvent) => {
    if (pageStore.isPublic) {
      return;
    }
    const normalizedCode = normalizeKeyCode(event.code);
    if (isModifierCode(normalizedCode)) {
      return;
    }

    const now = Date.now();
    const stroke = stringifyStrokeFromEvent(event, isMac);

    const isTimedOut = sequenceState.value.active && now - sequenceState.value.timestamp > SEQUENCE_TIMEOUT;
    if (isTimedOut) {
      resetSequenceState();
    }

    // Second stroke in sequence
    if (sequenceState.value.active && !isTimedOut) {
      const entry = trie.value.get(sequenceState.value.firstStroke);
      if (!entry || !(entry instanceof Map)) {
        return;
      }

      const shortcut = entry.get(stroke);
      if (shortcut) {
        if (!isShortcutAllowedOnPage(shortcut.id)) {
          resetSequenceState();
          return;
        }
        executeShortcut(shortcut, event, stroke);
      } else {
        resetSequenceState();
      }
      return;
    }

    // First stroke in sequence or single-stroke shortcut
    const entry = trie.value.get(stroke);
    if (!entry) {
      return;
    }

    if (entry instanceof Map) {
      sequenceState.value = { active: true, firstStroke: stroke, timestamp: now };
    } else {
      if (!isShortcutAllowedOnPage(entry.id)) {
        return;
      }
      executeShortcut(entry, event, stroke);
    }
  });
};
